import { parseFormData } from '@wyz/utils';
import { IWcwSearchFormSchema } from '~/purchase/schemas';
import { IWcwFilter, IWcwListState, IWcwOffer } from '~/purchase/types';
// eslint-disable-next-line @wyz/module/no-relative-import
import { DEFAULT_SEARCH_SORT } from './search';

export const canSubmitWcwSearch = (
  values: Partial<
    Pick<
      IWcwSearchFormSchema,
      'identifier' | 'rawIdentifier' | 'brand' | 'model' | 'seasons' | 'set'
    >
  >,
): boolean => {
  const hasReference = Boolean(
    values.identifier?.value ?? values.rawIdentifier,
  );
  const hasVehicle = Boolean(values.brand) && Boolean(values.model);
  const hasSeasons = Boolean(values.seasons?.length);

  return (hasReference && Boolean(values.set)) || (hasVehicle && hasSeasons);
};

type IWcwFilters = {
  [Key in keyof IWcwFilter]: (
    products: IWcwOffer[],
    value: { filters: IWcwFilter },
  ) => IWcwOffer[];
};

const getWcwProductFilters = (): IWcwFilters => ({
  wheelNames: (products, { filters }) =>
    filters.wheelNames.length === 0
      ? products
      : products.filter((p) => filters.wheelNames.includes(p.wheel.name)),
  rimDiameters: (products, { filters }) =>
    filters.rimDiameters.length === 0
      ? products
      : products.filter((p) =>
          filters.rimDiameters.includes(p.wheel.rimDiameter),
        ),
  rimTypes: (products, { filters }) =>
    filters.rimTypes.length === 0
      ? products
      : products.filter((p) => filters.rimTypes.includes(p.wheel.rimType)),
  brands: (products, { filters }) =>
    filters.brands.length === 0
      ? products
      : products.filter((p) => filters.brands.includes(p.tyre.brand.name)),
  versions: (products, { filters }) =>
    filters.versions.length === 0
      ? products
      : products.filter((p) =>
          p.compatibleVehicles.some((v) =>
            filters.versions.includes(v.version),
          ),
        ),
});

export function filterWcwProducts(
  products: IWcwOffer[],
  listState: IWcwListState,
): IWcwOffer[] {
  let result = [...products];
  Object.values(getWcwProductFilters()).forEach((filterer) => {
    result = filterer(result, { filters: listState.filters });
  });
  return result;
}

export function sortWcwProducts(
  products: IWcwOffer[],
  listState: IWcwListState,
): IWcwOffer[] {
  const result = [...products];
  const sort = listState.sort;
  if (!sort || sort === 'relevance') return result;

  let getters: Array<{ getter: (p: IWcwOffer) => number; asc: boolean }> = [];

  if (sort === 'price') {
    getters = [
      {
        getter: (p) =>
          p.offers.find((o) => o.offerMetadata.availableQuantity > 0)
            ?.offerMetadata.priceExclTax ?? Infinity,
        asc: true,
      },
    ];
  } else {
    getters = [
      {
        getter: (p) => {
          const date = p.offers[0]?.offerMetadata.deliveryDate;
          return date ? new Date(date).getTime() : Infinity;
        },
        asc: true,
      },
    ];
  }

  return result.sort((a, b) => {
    for (const { getter, asc } of getters) {
      const diff = getter(a) - getter(b);
      if (diff !== 0 && !isNaN(diff)) return asc ? diff : diff * -1;
    }
    return 0;
  });
}

export const getWcwSearchDefaultValues = (
  overrides?: Partial<IWcwSearchFormSchema>,
): Partial<IWcwSearchFormSchema> => ({
  vehicleTypeGroup: 'WCW',
  set: 4,
  ...overrides,
});

export const parseWcwSearchFromQs = (
  searchParams: URLSearchParams,
): Partial<IWcwSearchFormSchema> => {
  const qs = Array.from(searchParams.entries())
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const parsed = parseFormData<Partial<IWcwSearchFormSchema>>(qs);
  const overrides: Partial<IWcwSearchFormSchema> = {};

  if (parsed.brand) overrides.brand = parsed.brand;
  if (parsed.model) overrides.model = parsed.model;
  if (parsed.versions) overrides.versions = parsed.versions;
  if (parsed.seasons) overrides.seasons = parsed.seasons;
  if (parsed.set) overrides.set = parsed.set;

  const rawIdentifier = searchParams.get('rawIdentifier');
  if (rawIdentifier) overrides.rawIdentifier = rawIdentifier;

  const identifier = searchParams.get('identifier');
  if (identifier)
    overrides.identifier = { value: identifier, label: identifier };

  return overrides;
};

export const serializeWcwSearchToQs = (
  values?: Partial<IWcwSearchFormSchema> | null,
): Partial<Record<string, unknown>> => {
  if (!values) return {};
  const { vehicleTypeGroup: _vt, identifier, rawIdentifier, ...rest } = values;
  return {
    ...rest,
    ...(identifier?.value ? { identifier: identifier.value } : {}),
    ...(rawIdentifier ? { rawIdentifier } : {}),
  };
};

export const WCW_FILTER_DEFAULT: IWcwFilter = {
  wheelNames: [],
  rimDiameters: [],
  rimTypes: [],
  brands: [],
  versions: [],
};

export const WCW_LIST_STATE_DEFAULT: IWcwListState = {
  filters: WCW_FILTER_DEFAULT,
  sort: DEFAULT_SEARCH_SORT,
  searchQuery: undefined,
};

export const DEFAULT_WHEEL_IMAGE = '/img/default-wheel.png';
